import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faBell, faCog, faSignOutAlt, faChevronDown, faCheck, faEye, faBars, faCoins, faPlus, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useCredit } from '../context/CreditContext';

interface HeaderProps {
  onToggleSidebar?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  // @ts-ignore
  const { i18n, t } = useTranslation('header');
  const currentLang = lang || 'tr';
  const { business, logout } = useAuth();
  const { credit } = useCredit();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  // Check if we're on social media pages (with or without language prefix)
  const pathWithoutLang = location.pathname.replace(/^\/(tr|en)/, '');
  const isSocialMediaPage = pathWithoutLang.startsWith('/social-media');

  // Helper function to add language prefix to paths
  const getLocalizedPath = (path: string) => {
    return `/${currentLang}${path}`;
  };

  // Mock notifications data
  const notifications = [
    {
      id: 1,
      title: t('mockNotifications.newFollower.title'),
      message: t('mockNotifications.newFollower.message'),
      time: i18n.language === 'tr' ? '2 dakika önce' : '2 minutes ago',
      isRead: false,
      type: 'success'
    },
    {
      id: 2,
      title: t('mockNotifications.reportReady.title'),
      message: t('mockNotifications.reportReady.message'),
      time: i18n.language === 'tr' ? '1 saat önce' : '1 hour ago',
      isRead: false,
      type: 'info'
    },
    {
      id: 3,
      title: t('mockNotifications.aiRecommendation.title'),
      message: t('mockNotifications.aiRecommendation.message'),
      time: i18n.language === 'tr' ? '3 saat önce' : '3 hours ago',
      isRead: true,
      type: 'warning'
    },
    {
      id: 4,
      title: t('mockNotifications.systemUpdate.title'),
      message: t('mockNotifications.systemUpdate.message'),
      time: i18n.language === 'tr' ? '1 gün önce' : '1 day ago',
      isRead: true,
      type: 'info'
    },
    {
      id: 5,
      title: t('mockNotifications.accountConnection.title'),
      message: t('mockNotifications.accountConnection.message'),
      time: i18n.language === 'tr' ? '2 gün önce' : '2 days ago',
      isRead: true,
      type: 'success'
    }
  ];

  const navItems = [
    { to: '/social-media', label: t('socialMedia') },
    { to: '/web-site', label: t('webSite') },
    { to: '/ads', label: t('ads') },
    { to: '/blog', label: t('blog') }
  ];

  // Get localized nav items
  const localizedNavItems = navItems.map(item => ({
    ...item,
    to: getLocalizedPath(item.to)
  }));

  // Change language handler
  const changeLanguage = (newLang: string) => {
    if (i18n.language === newLang) return;

    const pathWithoutLang = location.pathname.replace(/^\/(tr|en)/, '') || '/social-media';
    const newPath = `/${newLang}${pathWithoutLang}`;

    i18n.changeLanguage(newLang);
    navigate(newPath);
    setIsLanguageOpen(false);
  };

  return (
    <header id="header" className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
      <div className="max-w-8xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-8">
            {/* Mobile Menu Button */}
            <button
              onClick={onToggleSidebar}
              className="md:hidden p-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
            >
              <FontAwesomeIcon icon={faBars} className="text-white/70" />
            </button>

            {/* <div className="flex items-center cursor-pointer" onClick={() => navigate(getLocalizedPath('/social-media'))}>
              <img 
                src="/logo.svg" 
                alt="Winnomi Logo" 
                className="w-8 h-8 md:w-10 md:h-10 object-contain"
              />
            </div> */}
            <nav className="hidden lg:flex items-center space-x-8">
              {localizedNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `text-white/80 transition-colors cursor-pointer ${isActive ? 'text-white font-semibold' : 'hover:text-white'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center space-x-4">
            {/* Credit Display & Add Credit Button - Only on social media pages */}
            {isSocialMediaPage && (
              <div className="hidden md:flex items-center space-x-3 bg-white/5 border border-white/10 rounded-xl px-4 py-2">
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faCoins} className="text-purple-400" />
                  <div className="text-white/80 text-sm">
                    <span className="font-semibold text-white">
                      {credit?.currentBalance !== undefined
                        ? Number(credit.currentBalance).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                        : '0'}
                    </span>
                    <span className="text-white/60 ml-1">{t('credit')}</span>
                  </div>
                </div>
                <div className="w-px h-6 bg-white/10"></div>
                <button
                  onClick={() => navigate(getLocalizedPath('/add-credit'))}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-purple-700 rounded-lg text-white text-sm font-medium hover:from-purple-600 hover:to-purple-800 transition-all"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-xs" />
                  <span>{t('addCredit')}</span>
                </button>
              </div>
            )}

            <div className="relative hidden md:block">
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                className="w-64 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute right-3 top-2.5 text-white/50"
              />
            </div>

            {/* Language Switcher */}
            <div className="relative hidden md:block">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center space-x-2 p-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
              >
                <FontAwesomeIcon icon={faGlobe} className="text-white/70" />
                <span className="text-white/70 text-sm font-medium">{currentLang.toUpperCase()}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-white/50 text-xs" />
              </button>

              {/* Language Dropdown */}
              {isLanguageOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsLanguageOpen(false)}
                  ></div>
                  <div className="absolute right-0 top-full mt-2 w-32 backdrop-blur-2xl bg-gray-900/95 border border-white/10 rounded-xl shadow-2xl z-50">
                    <div className="p-2">
                      <button
                        onClick={() => changeLanguage('tr')}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors text-sm ${i18n.language === 'tr'
                          ? 'bg-purple-500/20 text-white'
                          : 'text-white/80 hover:text-white hover:bg-white/10'
                          }`}
                      >
                        <span>TR</span>
                        {i18n.language === 'tr' && (
                          <FontAwesomeIcon icon={faCheck} className="text-purple-400" />
                        )}
                      </button>
                      <button
                        onClick={() => changeLanguage('en')}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors text-sm ${i18n.language === 'en'
                          ? 'bg-purple-500/20 text-white'
                          : 'text-white/80 hover:text-white hover:bg-white/10'
                          }`}
                      >
                        <span>EN</span>
                        {i18n.language === 'en' && (
                          <FontAwesomeIcon icon={faCheck} className="text-purple-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Notifications Dropdown */}
            <div className="relative">
              <button
                onClick={(e) => {
                  // On mobile (< 768px), navigate directly to notifications page
                  if (window.innerWidth < 768) {
                    navigate(getLocalizedPath('/notifications'));
                  } else {
                    // On desktop, show dropdown
                    setIsNotificationsOpen(!isNotificationsOpen);
                  }
                }}
                className="relative p-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
              >
                <FontAwesomeIcon icon={faBell} className="text-white/70" />
                {/* Notification Badge */}
                {notifications.filter(n => !n.isRead).length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {notifications.filter(n => !n.isRead).length}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown Menu */}
              {isNotificationsOpen && (
                <>
                  {/* Overlay */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsNotificationsOpen(false)}
                  ></div>

                  {/* Menu */}
                  <div className="absolute right-0 top-full mt-2 w-80 backdrop-blur-2xl bg-gray-900/95 border border-white/10 rounded-xl shadow-2xl z-50">
                    <div className="p-4 border-b border-white/10">
                      <div className="flex items-center justify-between">
                        <h3 className="text-white font-semibold">{t('notifications')}</h3>
                        <span className="text-white/60 text-sm">
                          {notifications.filter(n => !n.isRead).length} {t('unreadCount')}
                        </span>
                      </div>
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                      {notifications.slice(0, 5).map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 border-b border-white/5 hover:bg-white/5 transition-colors ${!notification.isRead ? 'bg-blue-500/5' : ''
                            }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-2 h-2 rounded-full mt-2 ${notification.type === 'success' ? 'bg-green-400' :
                              notification.type === 'warning' ? 'bg-yellow-400' :
                                'bg-blue-400'
                              }`}></div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className={`text-sm font-semibold ${!notification.isRead ? 'text-white' : 'text-white/80'
                                  }`}>
                                  {notification.title}
                                </h4>
                                {!notification.isRead && (
                                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                )}
                              </div>
                              <p className="text-white/70 text-sm mb-1">{notification.message}</p>
                              <p className="text-white/50 text-xs">{notification.time}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="p-3 border-t border-white/10">
                      <div className="flex space-x-2">
                        <button className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white/80 hover:text-white hover:bg-white/20 transition-colors text-sm">
                          <FontAwesomeIcon icon={faCheck} className="text-xs" />
                          <span>{t('markAllRead')}</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate(getLocalizedPath('/notifications'));
                            setIsNotificationsOpen(false);
                          }}
                          className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-gradient-to-r from-purple-500 to-purple-700 rounded-lg text-white hover:from-purple-600 hover:to-purple-800 transition-all duration-300 text-sm"
                        >
                          <FontAwesomeIcon icon={faEye} className="text-xs" />
                          <span>{t('allNotifications')}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-2 p-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg overflow-hidden">
                  <img
                    src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg"
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
                <FontAwesomeIcon icon={faChevronDown} className="text-white/70 text-xs" />
              </button>

              {/* Dropdown Menu */}
              {isProfileOpen && (
                <>
                  {/* Overlay */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsProfileOpen(false)}
                  ></div>

                  {/* Menu */}
                  <div className="absolute right-0 top-full mt-2 w-64 backdrop-blur-2xl bg-gray-900/95 border border-white/10 rounded-xl shadow-2xl z-50">
                    <div className="p-4 border-b border-white/10">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-xl overflow-hidden">
                          <img
                            src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg"
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h4 className="text-white font-semibold">{business?.name || t('business')}</h4>
                          <p className="text-white/60 text-sm">{business?.email || 'email@example.com'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-2">
                      <button
                        onClick={() => {
                          navigate(getLocalizedPath('/settings'));
                          setIsProfileOpen(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <FontAwesomeIcon icon={faCog} className="text-sm" />
                        <span>{t('settings')}</span>
                      </button>

                      <div className="border-t border-white/10 my-2"></div>

                      <button
                        onClick={() => {
                          logout();
                          navigate(getLocalizedPath('/login'));
                          setIsProfileOpen(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-3 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <FontAwesomeIcon icon={faSignOutAlt} className="text-sm" />
                        <span>{t('logout')}</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
